// Copyright 2025, University of Maryland, All Rights Reserved
/**
 * cm-coding: Administrators switch organization and workspace scope in the
 * Costs, Metrics, and Jobs dashboards. Use one shared button group so the same
 * scope action looks and behaves identically on all three screens. These are
 * buttons rather than tab panels because a selection changes the data for one
 * dashboard body instead of revealing separately mounted content.
 */
interface SegmentedTab<T extends string> {
  id: T
  label: string
}

interface SegmentedTabsProps<T extends string> {
  tabs: SegmentedTab<T>[]
  value: T
  onChange: (id: T) => void
}

export function SegmentedTabs<T extends string>({ tabs, value, onChange }: SegmentedTabsProps<T>) {
  return (
    <div className="flex gap-0.5 rounded-[9px] border border-slate-200 bg-[#eef2f7] p-[3px]">
      {tabs.map(tab => (
        <button
          key={tab.id}
          type="button"
          aria-pressed={value === tab.id}
          onClick={() => onChange(tab.id)}
          className={`rounded-[7px] px-3.5 py-1.5 text-[12.5px] font-[550] whitespace-nowrap transition-all duration-[120ms] ${
            value === tab.id
              ? 'bg-white text-slate-900 shadow-[0_1px_2px_rgba(2,8,23,0.08)]'
              : 'text-slate-500 hover:text-slate-700'
          }`}
        >
          {tab.label}
        </button>
      ))}
    </div>
  )
}
